/**
 * Protocol constants without zod, for the web client (`@handshake/protocol/constants`). The
 * package root re-exports all of them.
 */

/** Bump on any breaking change to the messages below. */
export const PROTOCOL_VERSION = 1;

/** Hard limit for one client message (bytes of JSON). Larger messages are rejected. */
export const MAX_CLIENT_MESSAGE_BYTES = 16 * 1024;

export const CARD_IDS = [
  "chat",
  "seal",
  "siphon",
  "poison",
  "inoculate",
  "audit",
  "tail",
  "plant",
  "bug",
  "confide",
] as const;

export const PIN_ICONS = ["siphon", "poison", "avoid"] as const;

/** Unambiguous letters for room codes (no I, L or O). */
export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ";
/** Unambiguous characters for handshake fallback codes and screen pairing codes. */
export const SHORT_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/** Server and protocol error codes (the engine adds its own rule error codes). */
export const ERROR_CODES = [
  "bad-json",
  "invalid-message",
  "too-large",
  "rate-limited",
  "not-authenticated",
  "already-authenticated",
  "unknown-session",
  "unsupported",
  "unknown-room",
  "not-host",
  "not-allowed",
  "dev-tools-off",
  "token-invalid",
  "token-expired",
  "token-used",
  "busy",
  "not-offering",
  "unknown-code",
  "screen-not-allowed",
  "analytics-off",
  "survey-closed",
  "survey-done",
  "server-error",
] as const;
export type ServerErrorCode = (typeof ERROR_CODES)[number];

/** WebSocket close codes used by the server. */
export const CLOSE_CODES = {
  /** No valid hello in time, or an unknown session. */
  unauthenticated: 4001,
  /** The session ended (the player left or was removed from the lobby). */
  sessionEnded: 4002,
  /** Too many invalid or rate-limited messages. */
  abuse: 4003,
  /** The server is shutting down; reconnect. */
  shutdown: 1012,
} as const;

// ---------------------------------------------------------------------------------------------
// Post-game survey (design doc §20.2)
// ---------------------------------------------------------------------------------------------

/** The five 1–5 ratings, in the order the survey asks them. */
export const SURVEY_RATING_KEYS = [
  "fun",
  "clarity",
  "fairness",
  "roleMattered",
  "endingEarned",
] as const;
export type SurveyRatingKey = (typeof SURVEY_RATING_KEYS)[number];

/** The question for each rating (1 = not at all, 5 = very much). */
export const SURVEY_RATING_QUESTIONS: Readonly<Record<SurveyRatingKey, string>> = {
  fun: "How fun was this game?",
  clarity: "How clear was what to do?",
  fairness: "How fair did the game feel?",
  roleMattered: "My role mattered.",
  endingEarned: "The ending felt earned.",
};

export const SURVEY_FREE_TEXT_QUESTION = "What was the most fun or most confusing moment?";

/** Free-text answers are cut to this many characters (after name scrubbing). */
export const SURVEY_TEXT_MAX = 500;

export const GENRE_EXPERIENCE = ["new", "some", "lots"] as const;
export type GenreExperience = (typeof GENRE_EXPERIENCE)[number];
